import { currentLanguage, dateLocale, isolateContent, localizeDigits, numberLocale, t, useLanguage } from '@/lib/i18n'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '@/features/auth/auth-context'
import { Icon } from '@/components/r5/glyphs'
import { formatBandValue, isShowableBand } from '@/domain/results/band-display'
import { formatTaskDate } from '@/features/tasks/tasks-format'
import {
  getNotifications,
  markAllNotificationsSeen,
  markNotificationRead,
  markNotificationsSeen,
} from './notifications-gateway'
import {
  applyBatchSeen,
  applyMarkAllSeen,
  applyMarkRead,
  emptyNotificationsState,
  fromListResponse,
  insertRealtimeNotification,
  unseenIds,
  type NotificationsState,
} from './notifications-state'
import type { RealtimeNotificationInsert, StudentNotification } from './notifications-types'
import './notifications.css'

const PAGE_SIZE = 10
const LIST_KEY = ['notifications', 'list'] as const

type WriteNotice = 'failed' | null

/** English keeps its compact "5m ago"; Arabic uses the full relative phrase. */
function ago(value: number, unit: Intl.RelativeTimeFormatUnit, english: string) {
  return currentLanguage() === 'ar'
    ? new Intl.RelativeTimeFormat(numberLocale(), { numeric: 'auto' }).format(-value, unit)
    : english
}

function relativeTime(iso: string) {
  const diffMinutes = Math.floor((Date.now() - new Date(iso).getTime()) / 60_000)
  if (Number.isNaN(diffMinutes) || diffMinutes < 1) return t('Just now')
  if (diffMinutes < 60) return ago(diffMinutes, 'minute', `${diffMinutes}m ago`)
  const diffHours = Math.floor(diffMinutes / 60)
  if (diffHours < 24) return ago(diffHours, 'hour', `${diffHours}h ago`)
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 7) return ago(diffDays, 'day', `${diffDays}d ago`)
  return new Date(iso).toLocaleDateString(dateLocale(), { day: 'numeric', month: 'short', year: 'numeric' })
}

const GRADED_MESSAGE = /^Your test '(.*)' has been graded\. Score: (\d+)\/(\d+)$/s

function finiteNumber(value: unknown): number | null {
  const number = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN
  return Number.isFinite(number) ? number : null
}

/**
 * Task notifications (ASSIGNMENTS-SPEC 6): the server names the kind and the facts in `metadata` and writes English prose
 * beside them. Both languages rebuild the sentence here from the codes, so the date reads in the interface language and the
 * teacher's title stays as authored. Anything incomplete is left to the ordinary handling below.
 */
function taskNotificationText(metadata: Record<string, unknown> | undefined): string | null {
  const kind = metadata?.kind
  const title = typeof metadata?.task_title === 'string' ? metadata.task_title.trim() : ''
  if (title === '' || (kind !== 'task_assigned' && kind !== 'task_due_soon' && kind !== 'task_overdue')) return null
  // The welcome test is stored with the English title "Onboarding test": the student reads it in their own language, not as authored text.
  const shown = metadata?.onboarding === true ? t('Welcome test') : isolateContent(title)
  if (kind === 'task_assigned') return t('New task: {0}', { 0: shown })
  if (kind === 'task_overdue') return t('{0} is overdue', { 0: shown })
  const due = typeof metadata?.due_at === 'string' ? new Date(metadata.due_at) : null
  if (!due || Number.isNaN(due.getTime())) return null
  return t('{0} is due {1}', { 0: shown, 1: formatTaskDate(due) })
}

/** A row written before 17 Oct 2026 says "... has been graded. Score: 0/40": a Band 0 by another name (owner rules 1 and 6). */
const OLD_SCORE_TEXT = /Score:\s*\d+\s*\/\s*\d+/
/** The current server sentence, kept readable for a realtime insert that carries no metadata. */
const READY_MESSAGE = /^Your results for '(.*)' are ready(?:: overall band (\d+(?:\.\d+)?))?$/s

/**
 * What a "results ready" notification says, in the interface language, from the stored facts and never from a raw score: the
 * title (metadata, or read out of either server sentence) and the overall band only when it is a real band (never 0). Null when
 * not even a title can be found.
 */
function resultsReadyText(notification: Pick<StudentNotification, 'message' | 'metadata'>): string | null {
  const metadata = notification.metadata ?? {}
  const message = notification.message ?? ''
  const old = GRADED_MESSAGE.exec(message)
  const ready = READY_MESSAGE.exec(message)
  const title = typeof metadata.test_title === 'string' && metadata.test_title.trim() !== '' ? metadata.test_title : (old?.[1] ?? ready?.[1])
  if (!title) return null
  const band = finiteNumber(metadata.overall_band) ?? finiteNumber(ready?.[2])
  if (currentLanguage() !== 'ar') {
    return isShowableBand(band) ? `Your results for '${title}' are ready: overall band ${formatBandValue(band)}` : `Your results for '${title}' are ready`
  }
  return isShowableBand(band)
    ? t('Your results for \u201c{0}\u201d are ready. Overall band {1}.', { 0: isolateContent(title), 1: formatBandValue(band) })
    : t('Your results for \u201c{0}\u201d are ready.', { 0: isolateContent(title) })
}

/**
 * The server writes notification prose in English. English shows it exactly as sent, EXCEPT a row from before 17 Oct 2026 that
 * still carries a raw score, which is rebuilt as "results ready" (adversarial review #9: "Score: 0/40" is a Band 0 by another
 * name). Arabic rebuilds the one message the server produces today (grading complete) from its metadata, or from the message
 * itself for a realtime insert that carries no metadata, and never reads a raw score out in either language. The test title
 * stays as authored. The three task messages are rebuilt from their codes in both languages.
 */
function notificationText(notification: Pick<StudentNotification, 'message' | 'metadata' | 'notification_type'>) {
  const task = taskNotificationText(notification.metadata)
  if (task !== null) return task
  const message = notification.message ?? ''
  const isGrading = notification.notification_type === 'grading_complete' || GRADED_MESSAGE.test(message) || READY_MESSAGE.test(message)
  if (currentLanguage() !== 'ar') {
    if (!OLD_SCORE_TEXT.test(message)) return notification.message
    return resultsReadyText(notification) ?? 'Your results are ready'
  }
  if (isGrading) {
    const ready = resultsReadyText(notification)
    if (ready !== null) return ready
  }
  return t('You have a new update. Open it to see the details.')
}

export function NotificationsBell() {
  useLanguage()
  const { demo } = useAuth()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const containerRef = useRef<HTMLDivElement>(null)
  const [open, setOpen] = useState(false)
  const [writeNotice, setWriteNotice] = useState<WriteNotice>(null)

  const query = useQuery({
    queryKey: LIST_KEY,
    queryFn: async () => fromListResponse(await getNotifications(PAGE_SIZE, 0)),
    enabled: !demo,
  })
  const state = demo ? emptyNotificationsState() : query.data

  // Realtime inserts from the existing bridge: prepend + bump counts locally
  // for an instant badge; the bridge separately invalidates ['notifications']
  // so the server list remains the authority.
  useEffect(() => {
    if (demo) return
    const onInsert = (event: Event) => {
      const detail = (event as CustomEvent<RealtimeNotificationInsert>).detail
      if (!detail?.id) return
      queryClient.setQueryData<NotificationsState>(LIST_KEY, (current) =>
        insertRealtimeNotification(current ?? emptyNotificationsState(), detail))
    }
    window.addEventListener('ielts-v2-notification', onInsert)
    return () => window.removeEventListener('ielts-v2-notification', onInsert)
  }, [demo, queryClient])

  useEffect(() => {
    if (!open) return
    const onPointerDown = (event: PointerEvent) => {
      if (!containerRef.current?.contains(event.target as Node)) setOpen(false)
    }
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    window.addEventListener('pointerdown', onPointerDown)
    window.addEventListener('keydown', onKeyDown)
    return () => {
      window.removeEventListener('pointerdown', onPointerDown)
      window.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  const recordWriteFailure = useCallback((error: unknown) => {
    setWriteNotice('failed')
  }, [])

  // Opening the panel marks the visible unseen items seen (Vue parity). Cache
  // state changes only on server success — a blocked write keeps the badge and
  // flags truthful instead of faking a cleared state.
  const markVisibleSeen = useCallback(async (current: NotificationsState) => {
    const ids = unseenIds(current)
    if (demo) return
    try {
      if (ids.length > 0) {
        const response = await markNotificationsSeen(ids)
        queryClient.setQueryData<NotificationsState>(LIST_KEY, (value) =>
          value ? applyBatchSeen(value, response.notifications) : value)
      }
      // Counts are account-wide; the first page can leave older unseen items
      // on the badge. Opening the panel should clear that leftover too.
      const leftover = queryClient.getQueryData<NotificationsState>(LIST_KEY)
      if ((leftover?.unseenCount ?? current.unseenCount) > 0) {
        await markAllNotificationsSeen()
        queryClient.setQueryData<NotificationsState>(LIST_KEY, (value) =>
          value ? applyMarkAllSeen(value) : value)
      }
      setWriteNotice(null)
    } catch (error) {
      recordWriteFailure(error)
    }
  }, [demo, queryClient, recordWriteFailure])

  const togglePanel = () => {
    const next = !open
    setOpen(next)
    if (next && state) void markVisibleSeen(state)
  }

  const onMarkAllSeen = async () => {
    try {
      await markAllNotificationsSeen()
      queryClient.setQueryData<NotificationsState>(LIST_KEY, (value) => (value ? applyMarkAllSeen(value) : value))
      setWriteNotice(null)
    } catch (error) {
      recordWriteFailure(error)
    }
  }

  const onNotificationClick = async (notification: StudentNotification) => {
    if (!demo && (!notification.is_read || !notification.is_seen)) {
      try {
        const updated = await markNotificationRead(String(notification.id))
        queryClient.setQueryData<NotificationsState>(LIST_KEY, (value) =>
          value ? applyMarkRead(value, updated) : value)
        setWriteNotice(null)
      } catch (error) {
        recordWriteFailure(error)
      }
    }
    if (notification.link) {
      setOpen(false)
      // The server link is a relative app path (e.g. /results/<id>); always
      // route internally, never treat it as an absolute URL.
      navigate(notification.link)
    }
  }

  const unseenCount = state?.unseenCount ?? 0

  return (
    <div className="notifications-bell" ref={containerRef}>
      <button
        type="button"
        className="notifications-bell__trigger"
        aria-label={unseenCount > 0 ? t("Notifications, {0} unseen", { "0": unseenCount }) : t("Notifications")}
        aria-expanded={open}
        onClick={togglePanel}
      >
        <Icon name="bell" size={16} />
        {unseenCount > 0 && (
          <span className="notifications-bell__badge" aria-hidden="true">{localizeDigits(unseenCount > 9 ? '9+' : String(unseenCount))}</span>
        )}
      </button>
      {open && (
        <section className="notifications-panel" aria-label={t("Notifications")}>
          <header>
            <h2>{t("Notifications")}</h2>
            {!demo && unseenCount > 0 && (
              <button type="button" onClick={() => void onMarkAllSeen()}>{t("Mark all as seen")}</button>
            )}
          </header>
          {writeNotice === 'failed' && (
            <p className="notifications-panel__notice" role="alert">{t("We could not update your notifications just now, so nothing has changed.")}</p>
          )}
          {demo && (
            <p className="notifications-panel__status" role="note">{t("The sample data has no notifications.")}</p>
          )}
          {!demo && query.isPending && (
            <p className="notifications-panel__status" role="status">{t("Loading notifications…")}</p>
          )}
          {!demo && query.isError && (
            <div className="notifications-panel__status" role="alert">
              <p>{t("Your notifications could not be loaded.")}</p>
              <button type="button" className="text-[var(--accent-deep)] font-semibold" onClick={() => void query.refetch()}>{t("Retry")}</button>
            </div>
          )}
          {!demo && state && !query.isError && state.notifications.length === 0 && (
            <p className="notifications-panel__status">{t("No notifications yet")}</p>
          )}
          {!demo && state && state.notifications.length > 0 && (
            <ul className="notifications-panel__list">
              {state.notifications.map((notification) => (
                <li key={notification.id}>
                  <button
                    type="button"
                    className="notifications-panel__item"
                    data-read={notification.is_read}
                    onClick={() => void onNotificationClick(notification)}
                  >
                    <i aria-hidden="true" data-unread={!notification.is_read} />
                    <span>
                      <p>{notificationText(notification)}</p>
                      <time dateTime={notification.created_at}>{relativeTime(notification.created_at)}</time>
                    </span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {!demo && state?.hasMore && (
            <p className="notifications-panel__more">{t("Showing your latest {0}. Older ones are still kept on your account.", { 0: PAGE_SIZE })}</p>
          )}
        </section>
      )}
    </div>
  )
}
